import type {ReactNode} from 'react';

/** Design item 5: every confirmation uses the app's dialog, with verb labels, never window.confirm or window.prompt. */
export type ConfirmActionOptions={
 title:string;
 description:ReactNode;
 /** A verb that says what will happen, for example "Discard changes" or "Void demand". */
 confirmLabel:string;
 cancelLabel?:string;
 tone?:'warning'|'danger';
 /** When set, the dialog asks for a written reason and only confirms once it is long enough. A minLength of 0 makes
  *  the reason optional (controls policy R1): confirming with a blank reason resolves '' and only cancelling resolves null. */
 reason?:{label:string;minLength:number;maxLength?:number};
};
export type ConfirmRequest=ConfirmActionOptions&{resolve:(reason:string|null)=>void};

let pending:ConfirmRequest|null=null;
const listeners=new Set<()=>void>();
const notify=()=>{for(const listener of listeners)listener();};

export function subscribeConfirmRequests(listener:()=>void){listeners.add(listener);return()=>{listeners.delete(listener);};}
export const currentConfirmRequest=()=>pending;
export function settleConfirmRequest(reason:string|null){const request=pending;pending=null;notify();request?.resolve(reason);}

function open(options:ConfirmActionOptions):Promise<string|null>{
 // Without a mounted host (for example a standalone page), fall back to the browser so the check is never skipped.
 if(!listeners.size){
  if(options.reason){const answer=window.prompt(`${options.title}\n\n${options.reason.label}`);if(answer===null)return Promise.resolve(null);const text=answer.trim();return Promise.resolve(text.length>=options.reason.minLength?text:null);}
  return Promise.resolve(window.confirm(options.title)?'':null);
 }
 pending?.resolve(null);
 return new Promise(resolve=>{pending={...options,resolve};notify();});
}

/** Resolves true when the person confirms, false when they cancel. */
export async function confirmAction(options:Omit<ConfirmActionOptions,'reason'>):Promise<boolean>{return (await open(options))!==null;}

/** Resolves the typed reason when the person confirms, or null when they cancel. */
export function confirmWithReason(options:ConfirmActionOptions&{reason:NonNullable<ConfirmActionOptions['reason']>}):Promise<string|null>{return open(options);}

/** The standard wording for throwing away unsaved work. */
export const confirmDiscard=(what:string)=>confirmAction({title:`Discard ${what}?`,description:'Your unsaved changes will be lost.',confirmLabel:'Discard changes',cancelLabel:'Keep editing',tone:'warning'});
